import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { InputBox } from '@backrs_ui/common-ui/src/molecules/Lubav/InputBox';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { ChatBox } from '@backrs_ui/grow-ui/src/pages/OfficeHours/molecules/ChatBox';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  closeMobileFab,
  openMobileFab,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState';

import { Box, SxProps, Theme } from '@mui/material';

import { ShellHeader } from './ShellHeader';
const DISMISS_THRESHOLD = 100;
const OVERLAY_TOP_OFFSET = '75px';

export const FAB_HEIGHT = '100px';

type MobileLubavFABProps = Readonly<{
  activeSession: string | null;
  sessionTitle: string | null;
  handleNewChat?: () => void;
  onRestartSession?: () => void;
  onNewOfficeHoursSession?: () => void;
  confirmNewChat?: boolean;
  isTyping: boolean;
  isStreaming: boolean;
  defaultExpanded?: boolean;
}>;

export const MobileLubavFAB = ({
  activeSession,
  sessionTitle,
  handleNewChat,
  onRestartSession,
  onNewOfficeHoursSession,
  confirmNewChat,
  isTyping,
  isStreaming,
  defaultExpanded = false,
}: MobileLubavFABProps) => {
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartY = useRef(0);
  const dragHandleRef = useRef<HTMLDivElement | null>(null);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef(pathname);
  const dispatch = useAppDispatch();
  const expanded = useAppSelector((state) => state.sessionState.mobileFabOpen);

  // On navigation: expand if landing on a defaultExpanded route, collapse otherwise
  useEffect(() => {
    if (pathname !== prevPathnameRef.current) {
      prevPathnameRef.current = pathname;
      dispatch(defaultExpanded ? openMobileFab() : closeMobileFab());
    }
  }, [pathname, defaultExpanded, dispatch]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    setIsDragging(true);
  }, []);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    if (dragOffset > DISMISS_THRESHOLD) {
      dispatch(closeMobileFab());
    }

    setDragOffset(0);
  }, [dragOffset, dispatch]);

  // React's onTouchMove is passive — must use a non-passive native listener to preventDefault.
  useEffect(() => {
    const node = dragHandleRef.current;

    if (!node) return;

    const handleNativeTouchMove = (e: TouchEvent) => {
      if (!isDragging) return;

      const delta = e.touches[0].clientY - dragStartY.current;

      if (delta > 0) {
        e.preventDefault();
        setDragOffset(delta);
      } else {
        setDragOffset(0);
      }
    };

    node.addEventListener('touchmove', handleNativeTouchMove, { passive: false });

    return () => node.removeEventListener('touchmove', handleNativeTouchMove);
  }, [isDragging]);

  return (
    <>
      <Box
        id="mobile-lubav-fab-bar"
        sx={fabBarSx}
        onClick={() => !expanded && dispatch(openMobileFab())}
      >
        <InputBox readOnly placeholder={appStrings.modules.inputBox.askLubav} />
      </Box>

      {/* Backdrop — tap to close */}
      {expanded && (
        <Box
          id="mobile-lubav-backdrop"
          sx={backdropSx}
          onClick={() => dispatch(closeMobileFab())}
        />
      )}

      {/* Expanded bottom sheet */}
      <Box sx={overlaySx(expanded, isDragging, dragOffset)}>
        {/* Drag handle + header — drag to dismiss */}
        <Box
          id="mobile-lubav-drag-zone"
          ref={dragHandleRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          sx={dragZoneSx}
        >
          <Box sx={dragHandleSx} />
          <ShellHeader
            title={sessionTitle}
            onNewChat={handleNewChat}
            onRestartSession={onRestartSession}
            onNewOfficeHoursSession={onNewOfficeHoursSession}
            confirmNewChat={confirmNewChat}
            disabled={isTyping || isStreaming}
          />
        </Box>

        <Box sx={contentContainerSx}>
          <ChatBox key={activeSession} supercoach={false} />
        </Box>
      </Box>
    </>
  );
};

const fabBarSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'fixed',
  bottom: 0,
  left: { xs: 0, sm: 'calc(var(--sidebar-gutter, 0px) - 1px)' },
  right: 0,
  height: FAB_HEIGHT,
  zIndex: 1199,
  display: 'flex',
  alignItems: 'flex-start',
  padding: backrs.spacing['spacing-lg'],
  pl: backrs.spacing['spacing-md'],
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  borderBottom: 'none',
  borderTopLeftRadius: backrs.radius['radius-2xl'],
  borderTopRightRadius: backrs.radius['radius-2xl'],
  cursor: 'pointer',
});

const overlaySx =
  (isExpanded: boolean, isDragging: boolean, dragOffset: number): SxProps<Theme> =>
  ({ backrs }) => ({
    position: 'fixed',
    top: OVERLAY_TOP_OFFSET,
    left: { xs: 0, sm: 'calc(var(--sidebar-gutter, 0px) - 1px)' },
    right: 0,
    bottom: 0,
    zIndex: 1199,
    backgroundColor: backrs.colors['Colors/Base/White'],
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    borderBottom: 'none',
    borderTopLeftRadius: backrs.radius['radius-2xl'],
    borderTopRightRadius: backrs.radius['radius-2xl'],
    transform: isExpanded ? `translateY(${dragOffset}px)` : 'translateY(100%)',
    transition: isDragging ? 'none' : 'transform 0.3s ease-in-out',
    pointerEvents: isExpanded ? 'auto' : 'none',
    overscrollBehaviorY: 'contain',
  });

const dragZoneSx: SxProps<Theme> = {
  touchAction: 'none',
  overscrollBehaviorY: 'contain',
};

const dragHandleSx: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '4px',
  backgroundColor: backrs.colors['Gray/300'],
  borderRadius: '2px',
  margin: `${backrs.spacing['spacing-sm']} auto`,
  flexShrink: 0,
  cursor: 'grab',
});

const backdropSx: SxProps<Theme> = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1198,
};

const contentContainerSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};
